/**
 * Peak Love — centre mark dispersion animation (GSAP).
 *
 * On hover or focus, each letter flies along a quadratic arc to its target position.
 * PEAK and LOVE form one connected mesh: row chains plus a zigzag between rows.
 * Segments clip to glyph boxes and trim in/out with stroke-dashoffset.
 */
(function () {
  'use strict';

  var PEAK_S = 1.0495;
  var LOVE_S = 1.0497;
  var LOVE_Y = 23.108;

  var TARGETS = [
    [-0.34, 52.85],
    [141.23, -0.59],
    [283.91, 52.92],
    [425.47, -0.59],
    [6.37, 124.31],
    [146.85, 124.31],
    [287.39, 124.24],
    [426.12, 124.31],
  ];
  var CENTRE = [214.6, 75.2];
  var BOW = [1, -1, 1, -1, -1, 1, -1, 1];

  var EDGES = [
    [0, 1], [1, 2], [2, 3],
    [4, 5], [5, 6], [6, 7],
    [0, 4], [4, 1], [1, 5], [5, 2], [2, 6], [6, 3], [3, 7],
  ];

  var DUR_IN = 2;
  var DUR_OUT = 1.7;
  var STAG = 0.05;
  var SPAN = 1 - STAG * 7;
  var GAP = -0.5;
  var EASE = 'power1.inOut';

  function clamp(v, a, b) {
    return v < a ? a : v > b ? b : v;
  }

  function easeInOutQuad(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function box(L, ux, uy) {
    var tx = ux !== 0 ? (L.bw + GAP) / Math.abs(ux) : 1e9;
    var ty = uy !== 0 ? (L.bh + GAP) / Math.abs(uy) : 1e9;
    var t = Math.min(tx, ty);
    return [ux * t, uy * t];
  }

  function initPeakLoveLogo(root, options) {
    options = options || {};
    if (typeof gsap === 'undefined') {
      console.error('PeakLoveLogo: GSAP is required. Load gsap before logo-animation.js.');
      return null;
    }

    var logoWrap = typeof root === 'string' ? document.querySelector(root) : root;
    if (!logoWrap) return null;

    var linkG = logoWrap.querySelector('#links');
    var peakG = logoWrap.querySelector('#peakG');
    var loveG = logoWrap.querySelector('#loveG');
    if (!linkG || !peakG || !loveG) return null;

    var letters = [];
    var segs = [];

    function grab(group, scale, offY) {
      var gs = group.querySelectorAll('.ltr');
      for (var i = 0; i < gs.length; i++) {
        letters.push({ el: gs[i], scale: scale, offY: offY });
      }
    }

    grab(peakG, PEAK_S, 0);
    grab(loveG, LOVE_S, LOVE_Y);

    for (var i = 0; i < letters.length; i++) {
      var L = letters[i];
      var bb = L.el.getBBox();
      L.bw = (bb.width * L.scale) / 2;
      L.bh = (bb.height * L.scale) / 2;
      L.hx = (bb.x + bb.width / 2) * L.scale;
      L.hy = (bb.y + bb.height / 2) * L.scale + L.offY;
      L.tx = TARGETS[i][0] - CENTRE[0];
      L.ty = TARGETS[i][1] - CENTRE[1];
      L.bow = BOW[i];
    }

    /* The letters disperse to roughly 507x169px out of a 106x42px logo, so the
       pointer sits outside #logoWrap for most of the animation and mouseleave
       reverses it long before it finishes - which is why the later segments
       never trimmed all the way in. Track the box the letters actually occupy
       when expanded, in svg user units, and hit-test against that instead.
       Deliberately not a DOM hit area: an element that size would sit over the
       header and swallow the nav's own pointer events. */
    var svgEl = linkG.ownerSVGElement || logoWrap.querySelector('svg');
    var spread = null;

    for (var b = 0; b < letters.length; b++) {
      var LB = letters[b];
      var bx0 = LB.hx + LB.tx - LB.bw;
      var bx1 = LB.hx + LB.tx + LB.bw;
      var by0 = LB.hy + LB.ty - LB.bh;
      var by1 = LB.hy + LB.ty + LB.bh;
      if (!spread) {
        spread = { x0: bx0, x1: bx1, y0: by0, y1: by1 };
      } else {
        spread.x0 = Math.min(spread.x0, bx0);
        spread.x1 = Math.max(spread.x1, bx1);
        spread.y0 = Math.min(spread.y0, by0);
        spread.y1 = Math.max(spread.y1, by1);
      }
    }

    for (var k = 0; k < EDGES.length; k++) {
      var p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      p.setAttribute('class', 'link');
      linkG.appendChild(p);
      segs.push({
        el: p,
        a: EDGES[k][0],
        b: EDGES[k][1],
        stag: Math.max(EDGES[k][0], EDGES[k][1]),
      });
    }

    var state = { lin: 0 };
    var progressTween = null;
    var hovered = false;
    var focused = false;

    function syncProgress() {
      tweenProgress(hovered || focused ? 1 : 0);
    }

    function applyLogo(l) {
      var pts = [];

      for (var i = 0; i < letters.length; i++) {
        var letter = letters[i];
        var e = easeInOutQuad(clamp((l - i * STAG) / SPAN, 0, 1));
        var mx = letter.tx * 0.5;
        var my = letter.ty * 0.5;
        var len = Math.hypot(letter.tx, letter.ty) || 1;
        var cxp = mx - (letter.ty / len) * len * 0.16 * letter.bow;
        var cyp = my + (letter.tx / len) * len * 0.16 * letter.bow;
        var u = 1 - e;
        var x = 2 * u * e * cxp + e * e * letter.tx;
        var y = 2 * u * e * cyp + e * e * letter.ty;
        letter.el.setAttribute(
          'transform',
          'translate(' + (x / letter.scale).toFixed(3) + ',' + (y / letter.scale).toFixed(3) + ')'
        );
        pts.push([letter.hx + x, letter.hy + y]);
      }

      for (var j = 0; j < segs.length; j++) {
        var S = segs[j];
        var a = pts[S.a];
        var b = pts[S.b];
        var t = easeInOutQuad(clamp((l - S.stag * STAG) / SPAN, 0, 1));

        if (t < 0.002) {
          S.el.setAttribute('d', '');
          S.el.style.opacity = 0;
          continue;
        }

        var dx = b[0] - a[0];
        var dy = b[1] - a[1];
        var d = Math.hypot(dx, dy);
        if (d < 0.001) {
          S.el.setAttribute('d', '');
          S.el.style.opacity = 0;
          continue;
        }

        var ux = dx / d;
        var uy = dy / d;
        var s1 = box(letters[S.a], ux, uy);
        var s2 = box(letters[S.b], -ux, -uy);
        var x1 = a[0] + s1[0];
        var y1 = a[1] + s1[1];
        var x2 = b[0] + s2[0];
        var y2 = b[1] + s2[1];
        var segLen = Math.hypot(x2 - x1, y2 - y1);

        if (segLen <= 0.01) {
          S.el.setAttribute('d', '');
          S.el.style.opacity = 0;
          continue;
        }

        S.el.setAttribute('d', 'M' + x1.toFixed(2) + ' ' + y1.toFixed(2) + 'L' + x2.toFixed(2) + ' ' + y2.toFixed(2));
        /* Both in user units - see the note on #links .link in style.css:
           the dash only stays in this space while the path has no
           vector-effect. */
        S.el.setAttribute('stroke-dasharray', segLen.toFixed(2));
        S.el.setAttribute('stroke-dashoffset', (segLen * (1 - t)).toFixed(2));
        S.el.style.opacity = clamp(t * 2.2, 0, 1);
      }
    }

    function tweenProgress(target, onComplete) {
      if (progressTween) progressTween.kill();

      var remaining = target === 1 ? 1 - state.lin : state.lin;
      var duration = remaining * (target === 1 ? DUR_IN : DUR_OUT);
      if (duration <= 0) {
        if (onComplete) onComplete();
        return;
      }

      progressTween = gsap.to(state, {
        lin: target,
        duration: duration,
        ease: EASE,
        overwrite: true,
        onUpdate: function () {
          applyLogo(state.lin);
        },
        onComplete: onComplete,
      });
    }

    var HOVER_PAD = 24;

    /* getScreenCTM rather than a scale guess: it is the exact user-unit ->
       client-px transform, so this stays right if the logo is resized. */
    function hoverBounds() {
      var r = logoWrap.getBoundingClientRect();
      var m = svgEl && svgEl.getScreenCTM && svgEl.getScreenCTM();
      var x0 = r.left, y0 = r.top, x1 = r.right, y1 = r.bottom;

      if (m && spread) {
        var cx = [spread.x0, spread.x1];
        var cy = [spread.y0, spread.y1];
        for (var i = 0; i < 2; i++) {
          for (var j = 0; j < 2; j++) {
            var px = m.a * cx[i] + m.c * cy[j] + m.e;
            var py = m.b * cx[i] + m.d * cy[j] + m.f;
            x0 = Math.min(x0, px); x1 = Math.max(x1, px);
            y0 = Math.min(y0, py); y1 = Math.max(y1, py);
          }
        }
      }

      return { x0: x0 - HOVER_PAD, y0: y0 - HOVER_PAD,
               x1: x1 + HOVER_PAD, y1: y1 + HOVER_PAD };
    }

    function onPointerMove(event) {
      var b = hoverBounds();
      if (event.clientX >= b.x0 && event.clientX <= b.x1 &&
          event.clientY >= b.y0 && event.clientY <= b.y1) return;
      onLeave();
    }

    function trackPointer(on) {
      var fn = on ? 'addEventListener' : 'removeEventListener';
      document[fn]('pointermove', onPointerMove, true);
      window[fn]('blur', onLeave);
    }

    var desktopHoverMq = window.matchMedia(
      window.PeakInput ? '(min-width: 992px)' : '(hover: hover) and (pointer: fine) and (min-width: 992px)'
    );
    var hoverBound = false;

    function canHoverLogo() {
      return desktopHoverMq.matches && (!window.PeakInput || window.PeakInput.canHover());
    }

    function onEnter() {
      if (!canHoverLogo()) return;
      if (hovered) return;
      hovered = true;
      trackPointer(true);
      syncProgress();
    }

    function onLeave() {
      if (!hovered) return;
      hovered = false;
      trackPointer(false);
      syncProgress();
    }

    function onFocus() {
      if (!canHoverLogo()) return;
      focused = true;
      syncProgress();
    }

    function onBlur() {
      focused = false;
      syncProgress();
    }

    function bindHover(on) {
      if (on && !hoverBound) {
        if (!logoWrap.hasAttribute('tabindex') && !logoWrap.hasAttribute('href')) {
          logoWrap.setAttribute('tabindex', '0');
        }
        logoWrap.addEventListener('mouseenter', onEnter);
        logoWrap.addEventListener('focus', onFocus);
        logoWrap.addEventListener('blur', onBlur);
        hoverBound = true;
        return;
      }
      if (!on && hoverBound) {
        logoWrap.removeAttribute('tabindex');
        logoWrap.removeEventListener('mouseenter', onEnter);
        logoWrap.removeEventListener('focus', onFocus);
        logoWrap.removeEventListener('blur', onBlur);
        hoverBound = false;
        onLeave();
        focused = false;
        trackPointer(false);
        tweenProgress(0);
      }
    }

    function syncHover() {
      bindHover(canHoverLogo());
    }

    syncHover();
    if (desktopHoverMq.addEventListener) {
      desktopHoverMq.addEventListener('change', syncHover);
    } else if (desktopHoverMq.addListener) {
      desktopHoverMq.addListener(syncHover);
    }
    if (window.PeakInput) window.PeakInput.onChange(syncHover);

    if (options.startExpanded) {
      state.lin = 1;
      applyLogo(1);
    } else {
      applyLogo(0);
    }

    return {
      playIn: function () {
        tweenProgress(1);
      },
      playOut: function (onComplete) {
        tweenProgress(0, onComplete);
      },
      destroy: function () {
        if (progressTween) progressTween.kill();

        logoWrap.removeEventListener('mouseenter', onEnter);
        trackPointer(false);
        logoWrap.removeEventListener('focus', onFocus);
        logoWrap.removeEventListener('blur', onBlur);
        hoverBound = false;

        linkG.replaceChildren();
        applyLogo(0);
      },
    };
  }

  function boot() {
    var hasPreloader = !!document.getElementById('preloader');
    var skipPreloader = false;

    try {
      skipPreloader = sessionStorage.getItem('peakLovePreloaderSeen') === '1';
    } catch (error) {
      skipPreloader = false;
    }

    window.peakLoveLogo = initPeakLoveLogo('#logoWrap', {
      startExpanded: hasPreloader && !skipPreloader,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.PeakLoveLogo = { init: initPeakLoveLogo };
})();
